import { useAuth } from 'react-oidc-context';
import { useAnonymousRead } from '@/lib/usePages';

/**
 * The credential (if any) that data hooks should call the API with, and whether
 * calling it is worth doing yet.
 *
 * Every read hook used to gate on `enabled: Boolean(token)` and throw
 * `not authenticated` without one. That was correct while the only way into the
 * application was signing in. On an instance that serves signed-out visitors it
 * is not: the request would succeed as the read-only anonymous role, but the
 * hook never fires it, so a guest sees an application of empty panels rather
 * than a refusal — the worst of both, because nothing says why.
 *
 * `ready` is the one place that rule lives. A hook asks whether to run; it does
 * not re-derive the answer, and a future change to what "signed out but served"
 * means does not have to find twenty-three call sites again.
 *
 * `token` stays `string | undefined` and is passed to `apiFetch` either way:
 * undefined sends no Authorization header, which is exactly what the anonymous
 * path expects.
 *
 * Not for admin or write hooks. Those should keep requiring a token — not as a
 * security boundary (the server owns that) but because firing a request that
 * will certainly be refused turns a hidden control into a console full of 403s.
 */
export function useApiSession(): { token: string | undefined; ready: boolean } {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const servesGuests = useAnonymousRead().data === true;

  return { token, ready: Boolean(token) || servesGuests };
}
